import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { parseCorrelationData } from '@/entities/pattern';

import { Card } from '@/shared/ui/card';
import { PatternCorrelationStatsTable } from '@/shared/ui/patterns';

import type { PatternStudy } from '../../model/types/pattern-study';
import { PatternLabTitle } from '../pattern-lab-title/pattern-lab-title';

import cls from './pattern-lab-correlation-stats.module.css';

interface PatternStudyCorrelationStatsProps {
    patterns: PatternStudy[];
}

export const PatternLabCorrelationStats = memo(
    ({ patterns }: PatternStudyCorrelationStatsProps) => {
        const { t } = useTranslation('pattern-studio');

        if (!patterns?.length) {
            return null;
        }

        // Check if any pattern has correlation data
        const hasAnyCorrelationData = patterns.some((patternStudy) => {
            const positiveCorrelationData = parseCorrelationData(
                patternStudy.pattern?.coef_pos_list,
            );
            const negativeCorrelationData = parseCorrelationData(
                patternStudy.pattern?.coef_neg_list,
            );

            return (
                positiveCorrelationData.length > 0 ||
                negativeCorrelationData.length > 0
            );
        });

        // Hide the card if no patterns have correlation data
        if (!hasAnyCorrelationData) {
            return null;
        }

        return (
            <Card
                data-tour="pattern-lab-correlation-stats"
                helpId="PATTERN_LAB_CORRELATION_STATS"
                isCollapsible
                isFollowingCursor={false}
                isWithoutPadding
                title={t('patternStats.correlationStats')}
            >
                <div className={cls.container}>
                    {patterns.map((patternStudy) => {
                        const positiveCorrelationData = parseCorrelationData(
                            patternStudy.pattern?.coef_pos_list,
                        );
                        const negativeCorrelationData = parseCorrelationData(
                            patternStudy.pattern?.coef_neg_list,
                        );

                        // Only render this pattern if it has correlation data
                        if (
                            positiveCorrelationData.length === 0 &&
                            negativeCorrelationData.length === 0
                        ) {
                            return null;
                        }

                        return (
                            <div
                                key={patternStudy.pattern.unique_id}
                                className={cls.item}
                            >
                                <Card
                                    accentColor={patternStudy.color}
                                    hasShadow={false}
                                    hasSnowflake={false}
                                    isWithoutPadding
                                >
                                    <div className={cls.titleContainer}>
                                        <PatternLabTitle
                                            title={
                                                patternStudy.name ??
                                                patternStudy.pattern.unique_id
                                            }
                                        />
                                    </div>

                                    <div className={cls.tablesContainer}>
                                        {positiveCorrelationData.length > 0 && (
                                            <PatternCorrelationStatsTable
                                                data={positiveCorrelationData}
                                                titleKey="patternStats.positiveCorrelatedVariable"
                                                variant="positive"
                                            />
                                        )}
                                        {negativeCorrelationData.length > 0 && (
                                            <PatternCorrelationStatsTable
                                                data={negativeCorrelationData}
                                                titleKey="patternStats.negativeCorrelatedVariable"
                                                variant="negative"
                                            />
                                        )}
                                    </div>
                                </Card>
                            </div>
                        );
                    })}
                </div>
            </Card>
        );
    },
);

PatternLabCorrelationStats.displayName = 'PatternStudyCorrelationStats';
